import * as THREE from 'three';
import { CARD_WIDTH, CARD_HEIGHT } from '../cardMesh';

// ── Dragonlord spirit echo ───────────────────────────────────────
//
// The battlefield read for "the Dragonlord has merged with its
// Dragon." Three layers, all parented to one group that follows the
// avatar card's transform:
//
//   1. SPIRIT CARD. A ghostly copy of the companion Dragon's art,
//      desaturated and pushed toward ember-amber. On invoke it
//      materialises high above the avatar and plunges down into it
//      (ease-in, slight fan-rotation settle); after impact it rests
//      fanned behind the avatar's top-left corner, hovering with a
//      slow bob and breathing opacity. Two cards visibly merged
//      into one unit, like a fanned hand.
//   2. IMPACT RING. A one-shot expanding shockwave ring on the
//      table surface, fired the moment the plunge lands.
//   3. EMBERS. A small loop of amber sparks drifting up from the
//      avatar's footprint while the merge is active.
//
// Lifecycle is driven by avatarAbilities.syncDragonSpiritEchoes:
// created when isDragonIdentityActive flips true, ticked every
// frame, told to fade via beginDragonSpiritEchoOutro when the
// identity reverts, disposed once the outro finishes.

const DESCEND_SECONDS = 0.85;
const OUTRO_SECONDS = 0.45;
const RING_SECONDS = 0.55;

// Rest pose of the spirit card in card-local space (x = card right,
// y = card top, z = up off the table once the group adopts the card
// mesh's lying-flat orientation).
const REST_X = -CARD_WIDTH * 0.30;
const REST_Y = CARD_HEIGHT * 0.22;
const REST_Z = 0.9;
const DESCEND_START_Z = 16;
const REST_SPIN = 0.18; // radians. The "fanned" angle

const SPIRIT_VERT = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

// Dragon art ghosted toward amber: luminance drives an ember ramp so
// the art stays recognisable while clearly reading as a spirit, with
// a slow vertical shimmer band passing through it.
const SPIRIT_FRAG = /* glsl */ `
  precision highp float;

  uniform sampler2D uMap;
  uniform float uTime;
  uniform float uOpacity;

  varying vec2 vUv;

  void main() {
    vec4 tex = texture2D(uMap, vUv);
    float lum = dot(tex.rgb, vec3(0.299, 0.587, 0.114));
    vec3 ember = mix(vec3(0.32, 0.13, 0.03), vec3(1.0, 0.82, 0.45), lum);
    // Keep a hint of the original art colour so the Dragon is
    // recognisable at a glance.
    vec3 col = mix(ember, tex.rgb, 0.25);

    // Slow shimmer band sweeping up the card.
    float band = smoothstep(0.12, 0.0, abs(fract(vUv.y * 0.9 - uTime * 0.18) - 0.5) - 0.32);
    col += vec3(0.55, 0.38, 0.12) * band * 0.5;

    // Soft vignette toward the card edges so the ghost has no hard
    // rectangular border.
    vec2 d = abs(vUv - 0.5) * 2.0;
    float edge = 1.0 - smoothstep(0.72, 1.0, max(d.x, d.y));

    float a = tex.a * uOpacity * edge;
    if (a < 0.004) discard;
    gl_FragColor = vec4(col, a);
  }
`;

const RING_VERT = SPIRIT_VERT;

const RING_FRAG = /* glsl */ `
  precision highp float;

  uniform float uProgress; // 0 → 1 over the ring's life

  varying vec2 vUv;

  void main() {
    vec2 p = (vUv - 0.5) * 2.0;
    float r = length(p);
    float radius = mix(0.12, 0.98, uProgress);
    float width = mix(0.10, 0.035, uProgress);
    float ring = smoothstep(width, 0.0, abs(r - radius));
    float fade = (1.0 - uProgress) * (1.0 - uProgress);
    float a = ring * fade;
    if (a < 0.004) discard;
    gl_FragColor = vec4(vec3(1.0, 0.78, 0.38) * a, a);
  }
`;

const EMBER_COUNT = 36;

const EMBER_VERT = /* glsl */ `
  uniform float uTime;
  uniform float uOpacity;

  attribute float aSeed;
  attribute float aPhase;

  varying float vAlpha;

  void main() {
    // Each ember loops a 2.6s rise; aPhase staggers the loop starts.
    float life = fract(uTime / 2.6 + aPhase);
    vec3 p = position;
    // Drift up plus a gentle seeded sway.
    p.z += life * 5.5;
    p.x += sin(uTime * 1.3 + aSeed * 17.0) * 0.5;
    p.y += cos(uTime * 1.1 + aSeed * 23.0) * 0.5;

    // Fade in fast, out slow over the loop.
    vAlpha = smoothstep(0.0, 0.12, life) * (1.0 - smoothstep(0.55, 1.0, life)) * uOpacity;

    vec4 mvPos = modelViewMatrix * vec4(p, 1.0);
    gl_PointSize = (1.6 + aSeed * 1.4) * (60.0 / -mvPos.z);
    gl_Position = projectionMatrix * mvPos;
  }
`;

const EMBER_FRAG = /* glsl */ `
  precision highp float;

  varying float vAlpha;

  void main() {
    float d = length(gl_PointCoord - 0.5) * 2.0;
    float a = (1.0 - smoothstep(0.4, 1.0, d)) * vAlpha;
    if (a < 0.004) discard;
    gl_FragColor = vec4(vec3(1.0, 0.72, 0.3) * a, a);
  }
`;

export function createDragonSpiritEcho(imageUrl) {
  const group = new THREE.Group();
  group.frustumCulled = false;

  // Spirit card. Hidden until the art texture lands so the plane
  // never paints an untextured black ghost.
  const spiritGeometry = new THREE.PlaneGeometry(CARD_WIDTH * 0.92, CARD_HEIGHT * 0.92);
  const spiritMaterial = new THREE.ShaderMaterial({
    uniforms: {
      uMap: { value: null },
      uTime: { value: Math.random() * 100 },
      uOpacity: { value: 0 },
    },
    vertexShader: SPIRIT_VERT,
    fragmentShader: SPIRIT_FRAG,
    transparent: true,
    depthWrite: false,
    depthTest: true,
    side: THREE.DoubleSide,
  });
  const spiritMesh = new THREE.Mesh(spiritGeometry, spiritMaterial);
  spiritMesh.frustumCulled = false;
  spiritMesh.visible = false;
  spiritMesh.position.set(REST_X, REST_Y, DESCEND_START_Z);
  spiritMesh.renderOrder = 210;
  group.add(spiritMesh);

  if (imageUrl) {
    const loader = new THREE.TextureLoader();
    loader.crossOrigin = 'anonymous';
    loader.load(imageUrl, (tex) => {
      tex.colorSpace = THREE.SRGBColorSpace;
      spiritMaterial.uniforms.uMap.value = tex;
      spiritMesh.visible = true;
    });
  }

  // Impact ring, flat on the table under the avatar.
  const ringGeometry = new THREE.PlaneGeometry(CARD_WIDTH * 2.6, CARD_WIDTH * 2.6);
  const ringMaterial = new THREE.ShaderMaterial({
    uniforms: { uProgress: { value: 0 } },
    vertexShader: RING_VERT,
    fragmentShader: RING_FRAG,
    transparent: true,
    depthWrite: false,
    depthTest: true,
    blending: THREE.AdditiveBlending,
    side: THREE.DoubleSide,
  });
  const ringMesh = new THREE.Mesh(ringGeometry, ringMaterial);
  ringMesh.frustumCulled = false;
  ringMesh.visible = false;
  ringMesh.position.z = 0.25;
  ringMesh.renderOrder = 205;
  group.add(ringMesh);

  // Ember loop seeded inside the avatar's footprint.
  const positions = new Float32Array(EMBER_COUNT * 3);
  const seeds = new Float32Array(EMBER_COUNT);
  const phases = new Float32Array(EMBER_COUNT);
  for (let i = 0; i < EMBER_COUNT; i++) {
    positions[i * 3] = (Math.random() * 2 - 1) * CARD_WIDTH * 0.42;
    positions[i * 3 + 1] = (Math.random() * 2 - 1) * CARD_HEIGHT * 0.42;
    positions[i * 3 + 2] = 0.3;
    seeds[i] = Math.random();
    phases[i] = Math.random();
  }
  const emberGeometry = new THREE.BufferGeometry();
  emberGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  emberGeometry.setAttribute('aSeed', new THREE.BufferAttribute(seeds, 1));
  emberGeometry.setAttribute('aPhase', new THREE.BufferAttribute(phases, 1));
  const emberMaterial = new THREE.ShaderMaterial({
    uniforms: {
      uTime: { value: Math.random() * 100 },
      uOpacity: { value: 0 },
    },
    vertexShader: EMBER_VERT,
    fragmentShader: EMBER_FRAG,
    transparent: true,
    depthWrite: false,
    depthTest: true,
    blending: THREE.AdditiveBlending,
  });
  const emberPoints = new THREE.Points(emberGeometry, emberMaterial);
  emberPoints.frustumCulled = false;
  emberPoints.renderOrder = 215;
  group.add(emberPoints);

  return {
    points: group,
    spiritMesh,
    spiritMaterial,
    spiritGeometry,
    ringMesh,
    ringMaterial,
    ringGeometry,
    emberPoints,
    emberMaterial,
    emberGeometry,
    elapsed: 0,
    ringStartedAt: null,
    outroStartedAt: null,
  };
}

// Tell the echo to fade out (identity reverted). The driver keeps
// ticking it until update() reports it finished.
export function beginDragonSpiritEchoOutro(effect) {
  if (effect && effect.outroStartedAt == null) {
    effect.outroStartedAt = effect.elapsed;
  }
}

const _tmpVec3 = new THREE.Vector3();
const _tmpEuler = new THREE.Euler();

/**
 * Per-frame tick. Returns false once the outro has fully faded.
 * the driver disposes the effect at that point.
 */
export function updateDragonSpiritEcho(effect, dt, cardMesh) {
  if (!effect || !cardMesh) return false;
  // Stay tied to the card: when region-view hides the avatar (surface
  // unit hidden in subsurface view, and vice-versa) the echo hides
  // with it instead of bleeding into the other region.
  effect.points.visible = cardMesh.visible;
  effect.elapsed += dt;
  const t = effect.elapsed;

  effect.spiritMaterial.uniforms.uTime.value += dt;
  effect.emberMaterial.uniforms.uTime.value += dt;

  // Follow the avatar card's transform; children live in card-local
  // space (x = right, y = top, z = up).
  cardMesh.getWorldPosition(_tmpVec3);
  effect.points.position.copy(_tmpVec3);
  const intent = cardMesh.userData?.intentEuler;
  const yaw = intent ? intent.z : 0;
  _tmpEuler.set(-Math.PI / 2, 0, yaw, 'XYZ');
  effect.points.rotation.copy(_tmpEuler);

  // ── Descend phase: plunge from above into the avatar ──
  const descend = Math.min(1, t / DESCEND_SECONDS);
  const easeIn = descend * descend * descend;
  const z = DESCEND_START_Z + (REST_Z - DESCEND_START_Z) * easeIn;
  // Settle into the fan angle as it lands.
  const spin = REST_SPIN * descend;
  // Gentle bob + breathing once merged.
  const merged = descend >= 1;
  const bob = merged ? Math.sin(t * 1.6) * 0.18 : 0;
  effect.spiritMesh.position.set(REST_X, REST_Y, z + bob);
  effect.spiritMesh.rotation.z = spin;

  let opacity = merged
    ? 0.5 + 0.1 * Math.sin(t * 1.9)
    : 0.85 * Math.min(1, t / 0.18);

  // Impact: fire the shockwave ring exactly once, when the plunge
  // lands.
  if (merged && effect.ringStartedAt == null) {
    effect.ringStartedAt = t;
    effect.ringMesh.visible = true;
  }
  if (effect.ringStartedAt != null) {
    const ringT = Math.min(1, (t - effect.ringStartedAt) / RING_SECONDS);
    effect.ringMaterial.uniforms.uProgress.value = ringT;
    if (ringT >= 1) effect.ringMesh.visible = false;
  }

  // Embers ramp in after the impact.
  let emberOpacity = merged ? Math.min(1, (t - DESCEND_SECONDS) / 0.6) : 0;

  // ── Outro: identity reverted, everything fades together ──
  let alive = true;
  if (effect.outroStartedAt != null) {
    const out = Math.min(1, (t - effect.outroStartedAt) / OUTRO_SECONDS);
    const fade = 1 - out;
    opacity *= fade;
    emberOpacity *= fade;
    // The spirit lifts away as it fades. The merge visibly undoes.
    effect.spiritMesh.position.z += out * 4;
    if (out >= 1) alive = false;
  }

  effect.spiritMaterial.uniforms.uOpacity.value = opacity;
  effect.emberMaterial.uniforms.uOpacity.value = emberOpacity;
  return alive;
}

export function disposeDragonSpiritEcho(effect) {
  if (!effect) return;
  effect.points.parent?.remove(effect.points);
  effect.spiritGeometry?.dispose();
  effect.ringGeometry?.dispose();
  effect.emberGeometry?.dispose();
  effect.spiritMaterial?.uniforms?.uMap?.value?.dispose?.();
  effect.spiritMaterial?.dispose();
  effect.ringMaterial?.dispose();
  effect.emberMaterial?.dispose();
}
